import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { createApiClient } from '../lib/api-client';
import type { paths } from '../lib/api-types';
import { useApiStore } from '../store/api';
import { useAuthStore } from '../store/auth';

type Conversation =
  paths['/v1/conversations']['get']['responses']['200']['content']['application/json']['conversations'][0];

export interface UseConversationsReturn {
  conversations: Conversation[];
  loading: boolean;
  error: Error | null;
  createConversation: () => Promise<Conversation>;
  creatingConversation: boolean;
  updateConversation: (id: string, name: string) => Promise<Conversation>;
  selectConversation: (id: string) => void;
  selectedConversation: Conversation | null;
  refreshConversations: () => Promise<void>;
}

/**
 * Conversation list management hook using TanStack Query
 *
 * @example
 * ```typescript
 * const {
 *   conversations,
 *   loading,
 *   createConversation,
 *   selectedConversation
 * } = useConversations();
 * ```
 */
export function useConversations(): UseConversationsReturn {
  const apiUrl = useApiStore((state) => state.apiUrl);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const queryClient = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Query key for conversations
  const queryKey = ['conversations'];

  // Fetch conversations using TanStack Query
  const {
    data: conversations = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const client = createApiClient(apiUrl);
      const { data, error: apiError } = await client.GET('/v1/conversations');

      if (apiError || !data) {
        throw new Error('Failed to load conversations');
      }

      return data.conversations;
    },
    enabled: isAuthenticated,
    staleTime: 5000, // Consider data fresh for 5 seconds
  });

  // Create conversation mutation (always uses "Untitled" name)
  const createConversationMutation = useMutation({
    mutationFn: async () => {
      const client = createApiClient(apiUrl);
      const { data, error: apiError } = await client.POST('/v1/conversations', {
        body: { name: 'Untitled' },
      });

      if (apiError || !data) {
        throw new Error('Failed to create conversation');
      }

      return data.conversation;
    },
    onSuccess: (newConversation) => {
      // Add the new conversation to the cache
      queryClient.setQueryData<Conversation[]>(queryKey, (old = []) => [newConversation, ...old]);
    },
  });

  // Update conversation mutation
  const updateConversationMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const client = createApiClient(apiUrl);
      const { data, error: apiError } = await client.PATCH('/v1/conversations/{id}', {
        params: { path: { id } },
        body: { name },
      });

      if (apiError || !data) {
        throw new Error('Failed to update conversation');
      }

      return data.conversation;
    },
    onSuccess: (updatedConversation) => {
      // Update the conversation in the cache
      queryClient.setQueryData<Conversation[]>(queryKey, (old = []) =>
        old.map((c) => (c.id === updatedConversation.id ? updatedConversation : c))
      );
    },
  });

  const selectConversation = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  const selectedConversation = conversations.find((c) => c.id === selectedId) || null;

  return {
    conversations,
    loading,
    error: error as Error | null,
    createConversation: async () => {
      const result = await createConversationMutation.mutateAsync();
      return result;
    },
    creatingConversation: createConversationMutation.isPending,
    updateConversation: async (id: string, name: string) => {
      const result = await updateConversationMutation.mutateAsync({ id, name });
      return result;
    },
    selectConversation,
    selectedConversation,
    refreshConversations: async () => {
      await refetch();
    },
  };
}
